// Shared planning (lot Entraînement + Accueil, 13/10): the 7-day strip and the day's small-group rows that lived on the home,
// moved AS IS (same DOM and classes, so the same hand-authored copycat composition and the same ACS look, through the
// .cc-home / .acs-home-brand scope it was written for). The selected day is the shared store's planningDay (also used by
// Réserver), never a local copy and never reset. Slot actions open the same slot in Réserver, where confirmation, credits,
// refusals and the waitlist live (no silent toggle); the link carries jour / creneau and its origin (retour=entrainement).
// Personal programme sessions have NO date in the fixtures: they are listed apart, « Sans horaire imposé », outside the
// timed planning, with their existing Commencer / Reprendre route; realised sessions are not listed (they belong to Mon suivi).
import { useMemo, useRef, type KeyboardEvent, type ReactNode } from "react";
import { Check, ChevronRight } from "lucide-react";
import { DAYS, TODAY_KEY, type Slot } from "./home-fixtures";
import { capitalize } from "./demo-context";
import { isoOfDayKey, store, useStore } from "./store";
import { CANONICAL_ID, CURRENT_WEEK, countedProgs, statusOf, useProgState } from "./programmations-state";

/** The displayed day: the shared planningDay when it is in the current week's strip, otherwise today (display only). */
export function usePlanningDay() {
  const shared = useStore();
  return DAYS.some(d => d.key === shared.planningDay) ? shared.planningDay : TODAY_KEY;
}

/** Days of the strip that hold something in the « mine » view (a booked small group or a dated programme session), with the
 *  same filters as DayRows view="mine". Home « Mon agenda » numerals (validated variant 3, 13/10). */
export function useMineDays() {
  const shared = useStore();
  useProgState();
  const dated = new Set(countedProgs(CURRENT_WEEK).flatMap(p => p.sessions.map(s => s.date)));
  return new Set(DAYS.filter(d => shared.slots.some(s => s.day === d.key && s.state === "booked") || dated.has(isoOfDayKey(d.key))).map(d => d.key));
}

/** Marked days of the agenda for a view. « mine » (home): booked small groups and dated programme sessions (useMineDays).
 *  « all » (Entraînement): the semantics of the former Entraînement strip, kept as is: days holding a booked small group or one
 *  on the member's waitlist. */
export function useAgendaDays(view: "mine" | "all") {
  const shared = useStore();
  const mine = useMineDays();
  const all = useMemo(() => new Set(shared.slots.filter(s => s.state === "booked" || shared.waitlist.includes(s.id)).map(s => s.day)), [shared.slots, shared.waitlist]);
  return view === "mine" ? mine : all;
}

/** « Mon agenda », validated variant 3 « Large numeral calendar » (acs-agenda-bold-1310, src 8940cb80 / dist CSS 3861476d,
 *  13/10), MOVED from home.tsx (HomeAgenda) to be shared, not cloned: seven large date numerals for the shared selected day
 *  (store.planningDay), the chosen column and the day's detail form ONE cream surface (the column bridges into the panel), a
 *  fine deep-gold mark under the chosen day. No visible day title nor week range: the date stays in the ARIA names (day buttons,
 *  detail region) and the section heading is for assistive technology only. The detail is DayRows for the given view and origin
 *  (« mine »: the home; « all »: Entraînement, every small group of the day and the dated programme sessions). `foot`: the link
 *  under the detail (home « Tout voir », Entraînement « Planning »). Arrows / Home / End move the focus between days; Enter or
 *  click selects. */
export function AgendaV3({ view, origin, heading, headingId, foot }: { view: "mine" | "all"; origin: "accueil" | "entrainement"; heading: string; headingId: string; foot?: ReactNode }) {
  const day = usePlanningDay();
  const marked = useAgendaDays(view);
  const field = useRef<HTMLDivElement>(null);
  const index = Math.max(0, DAYS.findIndex(d => d.key === day));
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    const btns = [...(field.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    const to = e.key === "ArrowLeft" ? i - 1 : e.key === "ArrowRight" ? i + 1 : e.key === "Home" ? 0 : e.key === "End" ? btns.length - 1 : null;
    if (to === null) return;
    e.preventDefault();
    btns[Math.max(0, Math.min(btns.length - 1, to))]?.focus();
  };
  return <section className="cc-home-v3" aria-labelledby={headingId}>
    {/* Feedback 13/10: no visible title (kept for assistive technology); the link moves under the day's detail. */}
    <h2 id={headingId} className="hp-sr">{heading}</h2>
    <div ref={field} className="cc-home-v3-field" role="group" aria-label="Semaine du 12 au 18 octobre" onKeyDown={keys}>
      {DAYS.map(d => <button key={d.key} type="button" className="cc-home-v3-day" aria-pressed={d.key === day} data-has={marked.has(d.key) || undefined}
        aria-label={`${capitalize(d.long)}${marked.has(d.key) ? ", séances prévues" : ""}`} onClick={() => store.setPlanningDay(d.key)}>
        <span aria-hidden="true">{d.letter}</span><b aria-hidden="true">{d.num}</b><i aria-hidden="true" />
      </button>)}
    </div>
    <div className="cc-home-v3-panel" role="region" aria-live="polite" aria-label={capitalize(DAYS[index]!.long)}
      data-edge={index === 0 ? "start" : index === DAYS.length - 1 ? "end" : undefined}>
      <DayRows origin={origin} view={view} />
    </div>
    {foot && <div className="hp-agenda-foot">{foot}</div>}
  </section>;
}

/** The selected day's rows: small groups (Réserver / Réservé / Complet / waitlist, through Réserver's confirmation, with the
 *  caller's origin in retour=) and the programme sessions with a DEMO date on that day (Terminée / Commencer / Reprendre,
 *  launch guard when no exercise is shared). Same markup and actions for Entraînement and the home. */
/** `view`: "all" (default, Entraînement: every small group of the day, available / full / booked) or "mine" (home, 13/10:
 *  only the small groups really booked, plus the day's assigned programme sessions in every state; empty → « Aucune séance
 *  prévue ce jour »). */
export function DayRows({ origin, view = "all" }: { origin: "entrainement" | "accueil"; view?: "all" | "mine" }) {
  const shared = useStore();
  useProgState();
  const day = usePlanningDay();
  const daySlots = shared.slots.filter(s => s.day === day && (view === "all" || s.state === "booked"));
  const openSlot = (slot: Slot) => { window.location.hash = `#/reserver?jour=${slot.day}&creneau=${slot.id}&retour=${origin}`; };
  const slotLabel = (s: Slot) => s.state === "booked" ? "Réservé" : s.state === "full" ? (s.waitlist ? (shared.waitlist.includes(s.id) ? "En attente" : "Liste d'attente") : "Complet") : "Réserver";
  const dayPersonal = countedProgs(CURRENT_WEEK).flatMap(p => p.sessions.map(s => ({ p, s, st: statusOf(p, s) }))).filter(x => x.s.date === isoOfDayKey(day));
  const launch = (sid: string) => { window.location.hash = sid === CANONICAL_ID ? "#/seance" : `#/seance?id=${sid}`; };
  return daySlots.length || dayPersonal.length
    ? <ul className="cc-home-slots">{daySlots.map(s => view === "mine"
      // Home only (13/10, « Mon agenda »): the booked small group is ONE whole-row button (time / text / discreet state text),
      // same openSlot → Réserver with retour=accueil, where any cancellation is confirmed; nothing is cancelled from here.
      ? <li key={s.id} className="cc-home-slot cc-home-slot--booked"><button type="button" className="cc-home-agenda-row" onClick={() => openSlot(s)}>
          <span className="cc-home-slot-time">{s.time}</span>
          <span className="cc-home-slot-text"><span className="cc-home-slot-title">{s.title}</span><span className="cc-home-slot-meta">{s.minutes} min · au studio</span></span>
          <span className="cc-home-agenda-state">{slotLabel(s)}</span>
        </button></li>
      : <li key={s.id} className="cc-home-slot">
        <span className="cc-home-slot-time">{s.time}</span>
        <span className="cc-home-slot-text"><span className="cc-home-slot-title">{s.title}</span><span className="cc-home-slot-meta">{s.minutes} min · au studio</span></span>
        <button type="button" className="cc-home-slot-btn" data-state={s.state} data-waitlist={s.waitlist || undefined}
          disabled={s.state === "full" && !s.waitlist} onClick={() => openSlot(s)}>{slotLabel(s)}</button>
      </li>)}
      {dayPersonal.map(({ p, s, st }) => <li key={s.id} className="cc-home-slot">
        <span className="cc-home-slot-time" aria-hidden="true" />
        <span className="cc-home-slot-text"><span className="cc-home-slot-title">{s.title}</span><span className="cc-home-slot-meta">{p.name} · {s.minutes} min</span>
          {!s.exercises?.length && st !== "done" && <span className="cc-home-slot-meta">Le détail des exercices n'est pas encore partagé par ton coach.</span>}</span>
        {st === "done"
            // Every view (13/10, Home approved pattern reused on Entraînement): a done session is a discreet « ✓ Terminée » text
            // (Lucide Check), a status, not a button nor a grey pill.
            ? <span className="cc-home-done"><Check size={14} strokeWidth={2.4} aria-hidden="true" />Terminée</span>
          : s.exercises?.length ? <button type="button" className="cc-home-slot-btn" onClick={() => launch(s.id)}>{st === "started" ? "Reprendre" : "Commencer"}</button> : null}
      </li>)}</ul>
    : <p className="cc-home-empty">{view === "mine" ? "Aucune séance prévue ce jour" : "Pas de cours ce jour."}</p>;
}

export function SharedPlanning() {
  useStore();
  useProgState();
  const day = usePlanningDay();
  const selected = DAYS.find(d => d.key === day)!;
  // Programme sessions without a date stay in « Sans horaire imposé » while not done (none this week).
  const todo = countedProgs(CURRENT_WEEK).flatMap(p => p.sessions.map(s => ({ p, s, st: statusOf(p, s) }))).filter(x => !x.s.date && x.st !== "done");
  const launch = (sid: string) => { window.location.hash = sid === CANONICAL_ID ? "#/seance" : `#/seance?id=${sid}`; };

  return <div className="cc-home acs-home-brand cc-planning">
    {/* Agenda (13/10): the validated home agenda V3 in the « all » view (every small group of the day: Réserver / Réservé /
        Complet / waitlist through Réserver's confirmation, plus dated programme sessions). « Planning » keeps its target. */}
    <AgendaV3 view="all" origin="entrainement" headingId="pl-cours-jour" heading={`Cours du ${selected.long.replace(" octobre", "")}`}
      foot={<button type="button" className="cc-seeall" onClick={() => { window.location.hash = `#/reserver?jour=${day}&retour=entrainement`; }}>Planning <ChevronRight size={13} strokeWidth={2.2} /></button>} />

    {todo.length > 0 && <section className="cc-home-section" aria-labelledby="pl-sans-horaire">
      <div className="cc-sectionhead"><h2 id="pl-sans-horaire">Sans horaire imposé</h2></div>
      <div className="cc-card cc-home-card"><ul className="cc-home-slots">{todo.map(({ p, s, st }) => <li key={s.id} className="cc-home-slot">
        <span className="cc-home-slot-text"><span className="cc-home-slot-title">{s.title}</span><span className="cc-home-slot-meta">{p.name} · {s.minutes} min</span>
          {/* Same guard and copy as Mes programmations: no exercise shared → nothing to play, no launch (Root fix 13/10). */}
          {!s.exercises?.length && <span className="cc-home-slot-meta">Le détail des exercices n'est pas encore partagé par ton coach.</span>}</span>
        {s.exercises?.length ? <button type="button" className="cc-home-slot-btn" onClick={() => launch(s.id)}>{st === "started" ? "Reprendre" : "Commencer"}</button> : null}
      </li>)}</ul></div>
    </section>}
  </div>;
}
